/** Promoted Highlights admin — editorial pins + per-etid marketing overrides. */

export type HighlightSourceType = 'entity' | 'collection' | 'search' | 'cms';

export interface HighlightSourceRefEntity {
  eid: number;
}
export interface HighlightSourceRefCollection {
  ecolid: number;
}
export interface HighlightSourceRefSearch {
  query: Record<string, unknown>;
}
export interface HighlightSourceRefCms {
  cmsoid: number;
}
export type HighlightSourceRef =
  | HighlightSourceRefEntity
  | HighlightSourceRefCollection
  | HighlightSourceRefSearch
  | HighlightSourceRefCms;

/** An editorial pin — content pinned to the top of a card (etid) via the waterfall. */
export interface HighlightPin {
  hpid: number;
  etid: number;
  source_type: HighlightSourceType;
  source_ref: HighlightSourceRef;
  rank: number | null;
  start_at: string | null;
  end_at: string | null;
  date_added: string | null;
}

export interface CreateHighlightPinPayload {
  etid: number;
  source_type: HighlightSourceType;
  source_ref: HighlightSourceRef;
  rank?: number | null;
  start_at?: string | null;
  end_at?: string | null;
}

/** Per-etid marketing copy / cover override for a highlight card. */
export interface HighlightOverride {
  etid: number;
  title: string | null;
  subtitle: string | null;
  cover_asid: number | null;
  deeplink: string | null;
  date_modified: string | null;
}

export interface UpsertHighlightOverridePayload {
  etid: number;
  title?: string | null;
  subtitle?: string | null;
  cover_asid?: number | null;
  deeplink?: string | null;
}

/** Drives the source-type picker + the shape of `source_ref` for each option. */
export const HIGHLIGHT_SOURCE_TYPES: {
  value: HighlightSourceType;
  label: string;
  /** Friendly noun for the ID field, e.g. "Listing ID". */
  fieldLabel: string;
  refKey: 'eid' | 'ecolid' | 'query' | 'cmsoid';
  hint: string;
  /** Technical option kept out of the way for non-technical users. */
  advanced?: boolean;
}[] = [
  { value: 'entity', label: 'A specific listing', fieldLabel: 'Listing ID', refKey: 'eid', hint: 'Feature one exact listing.' },
  { value: 'collection', label: 'A saved collection', fieldLabel: 'Collection ID', refKey: 'ecolid', hint: 'Feature a collection you curated earlier.' },
  { value: 'cms', label: 'A content page', fieldLabel: 'Content page ID', refKey: 'cmsoid', hint: 'Feature the listing a content page is about.' },
  { value: 'search', label: 'A search result', fieldLabel: 'Search query', refKey: 'query', hint: 'Advanced: feature whatever matches a saved search.', advanced: true },
];

/** One-line summary of a pin's target for table rows. */
export const describeSourceRef = (
  pin: Pick<HighlightPin, 'source_type' | 'source_ref'>,
): string => {
  const ref = pin.source_ref as unknown as Record<string, unknown>;
  switch (pin.source_type) {
    case 'entity':
      return `eid ${ref?.eid ?? '—'}`;
    case 'collection':
      return `ecolid ${ref?.ecolid ?? '—'}`;
    case 'cms':
      return `cmsoid ${ref?.cmsoid ?? '—'}`;
    case 'search':
      return 'Elasticsearch query';
    default:
      return '—';
  }
};

export const normalizeHighlightPin = (raw: any): HighlightPin => ({
  hpid: Number(raw?.hpid),
  etid: Number(raw?.etid),
  source_type: raw?.source_type,
  source_ref: raw?.source_ref ?? {},
  rank: raw?.rank ?? null,
  start_at: raw?.start_at ?? null,
  end_at: raw?.end_at ?? null,
  date_added: raw?.date_added ?? null,
});

/** Returns `null` for an empty/absent override so the editor renders a clean slate. */
export const normalizeHighlightOverride = (raw: any): HighlightOverride | null => {
  if (!raw || typeof raw !== 'object') return null;
  const empty =
    raw.title == null &&
    raw.subtitle == null &&
    raw.cover_asid == null &&
    raw.deeplink == null;
  if (raw.etid == null && empty) return null;
  return {
    etid: Number(raw.etid),
    title: raw.title ?? null,
    subtitle: raw.subtitle ?? null,
    cover_asid: raw.cover_asid ?? null,
    deeplink: raw.deeplink ?? null,
    date_modified: raw.date_modified ?? null,
  };
};
